import {
    Box,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroup,
    Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import CustomAutocomplete from "../../../shared/AutoComplete";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomTextField from "../../../shared/CustomTextFeild";
import { RootState } from "../../../store/store";
import basestyle from "../../CSS/Base.module.css";
import ConfirmationPopup from "../../PopUp/BudgetPopUp";
import CancelPopUp from "../../PopUp/PopUp";
import { checkForecastChartAccountsExist, copychartAccountDetailsByYear, getAllFinancialYear } from "../services/Financials/budget.service";
import { SectionTitle, StyledInputLabel, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../StyledComponents/Container";
import { notifyUser } from "../Utils/notifyUser";

const NewBudget = () => {
    const [budgetYear, setBudgetYear] = useState<string>("");
    const [type, setType] = useState(7);
    const [redioType, setRedioType] = useState("church");
    const [message, setMessage] = useState("");
    const [financialYearsList, SetFinancialYearsList] = useState<string[]>([]);
    const [copyFrom, setCopyFrom] = useState("");
    const navigate = useNavigate();
    const [openPopUp, setOpenPopUp] = useState(false);
    const [reopenOrReplacePopUp, setReopenOrReplacePopUp] = useState(false);
    const [year, setYear] = useState(constants.SELECT_PLACEHOLDER);
    const [budgetYearError, setBudgetYearError] = useState<string>("");
    const [copyFromError, setCopyFromError] = useState<string>("");
    const userDetails = useSelector((state: RootState) => state.auth);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const [displayedYear, setDisplayedYear] = useState('');

    async function handleContinue() {
        let isValid = true;
        setBudgetYearError("");
        setCopyFromError("");

        if (!budgetYear || budgetYear.length !== 4) {
            setBudgetYearError(translate('enterValidBudgetYear'));
            isValid = false;
        } else if (parseInt(budgetYear) <= 2021) {
            setBudgetYearError(translate('enterTheBudgetYearMoreThan2021'));
            isValid = false;
        }

        if (year === constants.SELECT_PLACEHOLDER) {
            setCopyFromError(translate('pleaseSelectAValidFinancialYearToCopyFrom'));
            isValid = false;
        }

        if (isValid) {
            const formattedYear = `FY${budgetYear.slice(-2)}`;
            setDisplayedYear(formattedYear);

            try {
                const response = await copychartAccountDetailsByYear(year, formattedYear, type);
                if (response?.status === constants.STATUS_CODES.SUCCESS && response?.data?.data?.masterChartAccountsVOList) {
                    let responseData = response;
                    navigate("/budgetAccountDetails", {
                        state: {
                            type: type,
                            budgetYear: formattedYear,
                            copyFrom: year,
                            method: "Regular",
                            copychartAccountResponse: response?.data?.data?.masterChartAccountsVOList ? response?.data?.data?.masterChartAccountsVOList : null
                        },
                    });
                } else {
                    setMessage(response.data.message);

                    const responseList = await checkForecastChartAccountsExist(formattedYear, type, userDetails.clientId);
                    if (responseList?.status === constants.STATUS_CODES.SUCCESS && responseList?.data?.data === constants.API_RESPONSE_TYPE_SUCCESS) {
                        setReopenOrReplacePopUp(true);
                    } else {
                        notifyUser(responseList.data.message, 'error', translate('newBudget'));
                    }
                }
            } catch (error) {
                notifyUser(constants.ERROR_VALIDATION_MESSAGE_ERROR_OCCURRED_WHILE_FETCHING_THE_MASTER_CHART_ACCOUNT_DETAILES, 'error', translate('newBudget'));
            }
        }
    }

    useEffect(() => {
        if (year !== constants.SELECT_PLACEHOLDER) {
            setCopyFromError("");
        }
        if (budgetYear && budgetYear.length > 3) {
            setBudgetYearError("");
        }
    }, [year, budgetYear]);

    const handletype = (value: any) => {
        setRedioType(value);
        setType(value === "school" ? 4 : 7);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        if (/^\d{0,4}$/.test(newValue)) {
            setBudgetYear(newValue);
            setDisplayedYear(newValue);
        }
    };

    const handleBlur = () => {
        if (budgetYear) {
            const yea = budgetYear.toString().slice(-2);
            const financialYear = `FY${yea}`;
            setDisplayedYear(financialYear);
        }
    };

    const handleFocus = () => {
        setDisplayedYear(budgetYear);
    };

    const handleCancel = () => {
        setOpenPopUp(true);
        setReopenOrReplacePopUp(false);
    };

    const handlePopUpClose = () => {
        setOpenPopUp(false);
        setReopenOrReplacePopUp(false);
    };

    function handleConfirm() {
        navigate("/budgetListing");
    }

    async function handleReopenConfirm() {
        navigate("/budgetAccountDetails", {
            state: {
                type: type,
                budgetYear: displayedYear,
                copyFrom: year,
                method: "Reopen",
                copychartAccountResponse: null
            },
        });
    }

    function handleReplaceConfirm() {
        navigate("/budgetAccountDetails", {
            state: {
                type: type,
                budgetYear: displayedYear,
                copyFrom: year,
                method: "Replace",
                copychartAccountResponse: null
            },
        });
    }

    useEffect(() => {
        if (type) {
            getParticipantForecastStatusInfo();
        }
    }, [type]);

    async function getParticipantForecastStatusInfo() {
        try {
            const responseList = await getAllFinancialYear(type, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                const updatedFinancialYearsList = [constants.SELECT_PLACEHOLDER, ...responseList?.data?.data];
                SetFinancialYearsList(updatedFinancialYearsList);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        }
    }

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('newBudget')}</SectionTitle>
                    <Box sx={{ mt: 3, ml: 2 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <CustomTextField
                                    labelText={translate('budgetYear')}
                                    value={displayedYear}
                                    required
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onFocus={handleFocus}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ maxLength: 4 }}
                                    error={!!budgetYearError}
                                />
                                {budgetYearError && (
                                <FormHelperText  sx={{mt:-1.5}} error>
                                 {budgetYearError}
                                </FormHelperText>
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Typography sx={{ mt: 5, width: "100%" }}>({translate('budgetYearHint')})</Typography>
                            </Grid>
                            <Grid size={12} sx={{ display: 'flex' }}>
                                <StyledInputLabel sx={{ mt: 1.5, mr: 8 }} shrink htmlFor="Type"  >
                                    {translate('type')} <span style={{ color: 'red' }}>*</span>
                                </StyledInputLabel>
                                <RadioGroup row value={redioType} onChange={(e) => handletype(e.target.value)}>
                                    <FormControlLabel value="church" control={<Radio />} label={translate('church')} />
                                    <FormControlLabel value="school" control={<Radio />} label={translate('school')} />
                                </RadioGroup>
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <CustomAutocomplete
                                    labelText={translate('copyFrom')}
                                    required
                                    options={financialYearsList || []}
                                    value={year}
                                    onChange={(event, newValue) => setYear(newValue ?? constants.SELECT_PLACEHOLDER)}
                                    getOptionLabel={(option) => option}
                                    isOptionEqualToValue={(option, value) => option === year}
                                    placeholder={translate('copyFrom')}
                                    error={!!copyFromError}
                                />
                                {copyFromError && (
                                <FormHelperText error>
                                {copyFromError}
                                </FormHelperText>
                                )}
                            </Grid>
                        </Grid>
                        <Box className={basestyle.buttoncontainer} sx={{ mt: 3 }}>
                            <ContainedButton onClick={handleContinue}>{translate('continue')}</ContainedButton>
                            <OutlinedButton onClick={handleCancel}>{translate('cancel')}</OutlinedButton>
                        </Box>
                    </Box>

                    <CancelPopUp
                        open={openPopUp}
                        popupTitle={translate('confirmCancel')}
                        handleClose={handlePopUpClose}
                        saveChanges={handleConfirm}
                        message={translate('doYouWantToCancelChanges?')}
                    />

                    <ConfirmationPopup
                        open={reopenOrReplacePopUp}
                        popupTitle={translate('confirmationPopup')}
                        handleClose={handlePopUpClose}
                        saveReplaceChanges={handleReplaceConfirm}
                        saveReopenChanges={handleReopenConfirm}
                        message={message}
                    />
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    );
};

export default NewBudget;
